import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Container, Box, TextField, Button, Slider, Typography, Paper, List, ListItem,
  ListItemButton, ListItemText, FormControl, InputLabel, Select, MenuItem, Chip,
  CircularProgress, Alert, Link, Divider, InputAdornment, Collapse, Rating
} from '@mui/material';
import { Search, Icecream, Storefront, Place, MyLocation, RateReview, ExpandMore, ExpandLess } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import TituloPagina from './utils/TituloPagina.jsx';
import { API_BASE_URL, GRIDO_LOCALES, GRIDO_COMPETIDORES, GRIDO_GEOCODIFICAR, GRIDO_RESENAS, GRIDO_POBLACION } from '../services/apiConfig';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const GRIDO_COLOR = '#e2001a';   // rojo Grido
const COMP_COLOR = '#1565c0';    // azul competidores

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

// Texto secundario con la ubicación: dirección, barrio y ciudad; si no hay
// ninguno (OSM sin datos), cae a las coordenadas para que igual se distingan.
const ubicacionDe = (p) => {
  if (!p) return '';
  const partes = [p.direccion, p.barrio, p.ciudad].map((x) => (x || '').trim()).filter(Boolean);
  if (partes.length) return partes.join(', ');
  if (p.lat != null && p.lng != null) return `${Number(p.lat).toFixed(4)}, ${Number(p.lng).toFixed(4)}`;
  return 'Ubicación sin datos';
};

function GridoCompetencia() {
  const [zona, setZona] = useState('');
  const [locales, setLocales] = useState([]);
  const [gridoSel, setGridoSel] = useState('');       // osm_id del Grido elegido
  const [direccion, setDireccion] = useState('');     // dirección escrita a mano
  const [centroManual, setCentroManual] = useState(null); // punto por dirección o clic
  const [cuadras, setCuadras] = useState(5);
  const [competidores, setCompetidores] = useState([]);
  const [loadingLocales, setLoadingLocales] = useState(false);
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [loadingComp, setLoadingComp] = useState(false);
  const [error, setError] = useState('');
  const [resenas, setResenas] = useState({}); // osm_id -> {open, loading, data, error}
  const [poblacion, setPoblacion] = useState({ loading: false, data: null }); // {loading,data,error}

  const mapRef = useRef(null);       // instancia Leaflet
  const layerRef = useRef(null);     // capa de marcadores
  const containerRef = useRef(null); // div del mapa

  const gridoObj = locales.find((l) => l.osm_id === gridoSel) || null;
  // Punto de referencia efectivo: el manual (dirección/clic) tiene prioridad.
  const centro = centroManual || gridoObj;

  const buscarCompetidores = useCallback(async (ref, nCuadras) => {
    if (!ref) return;
    setLoadingComp(true);
    setError('');
    try {
      const res = await axios.get(API_BASE_URL + GRIDO_COMPETIDORES, {
        params: { lat: ref.lat, lng: ref.lng, cuadras: nCuadras },
        headers: authHeader(),
      });
      const data = res.data?.data ?? {};
      setCompetidores(data.competidores ?? []);
    } catch (e) {
      setError(e.response?.data?.message || 'Error al buscar competidores.');
      setCompetidores([]);
    } finally {
      setLoadingComp(false);
    }
  }, []);

  const cargarPoblacion = useCallback(async (ref, nCuadras) => {
    if (!ref) return;
    setPoblacion({ loading: true, data: null });
    try {
      const res = await axios.get(API_BASE_URL + GRIDO_POBLACION, {
        params: { lat: ref.lat, lng: ref.lng, cuadras: nCuadras },
        headers: authHeader(),
      });
      setPoblacion({ loading: false, data: res.data?.data ?? null });
    } catch (e) {
      setPoblacion({ loading: false, data: null, error: e.response?.data?.message || 'Error al obtener población.' });
    }
  }, []);

  // Inicializar el mapa una sola vez (incluye el manejo de clic para fijar punto).
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, { scrollWheelZoom: true }).setView([-31.42, -64.18], 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);

    map.on('click', async (e) => {
      const { lat, lng } = e.latlng;
      setGridoSel('');
      setCentroManual({ osm_id: 'manual', nombre: 'Punto marcado', lat, lng, direccion: '', barrio: '', ciudad: '' });
      try {
        const res = await axios.get(API_BASE_URL + GRIDO_GEOCODIFICAR, { params: { lat, lng }, headers: authHeader() });
        const d = res.data?.data;
        if (d) setCentroManual({ osm_id: 'manual', nombre: d.nombre || 'Punto marcado', lat: d.lat, lng: d.lng, direccion: d.direccion, barrio: d.barrio, ciudad: d.ciudad });
      } catch { /* mantenemos el punto sin etiqueta */ }
    });

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  // Redibujar marcadores cuando cambia el centro o los competidores.
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    if (!centro) return;

    const bounds = [];
    const esManual = centro.osm_id === 'manual';

    L.circleMarker([centro.lat, centro.lng], {
      radius: 11, color: '#fff', weight: 2, fillColor: GRIDO_COLOR, fillOpacity: 1,
    })
      .bindPopup(`<strong>${escapeHtml(centro.nombre)}</strong>` +
        `<br/><span style="color:${GRIDO_COLOR}">${esManual ? 'Punto de análisis' : 'Grido (local)'}</span>` +
        `<br/>${escapeHtml(ubicacionDe(centro))}`)
      .addTo(layer);
    bounds.push([centro.lat, centro.lng]);

    competidores.forEach((c) => {
      const popup = `<strong>${escapeHtml(c.nombre)}</strong><br/>` +
        `<span style="color:#666">a ${c.distancia} m</span>` +
        `<br/>${escapeHtml(ubicacionDe(c))}` +
        (c.telefono ? `<br/>Tel: ${escapeHtml(c.telefono)}` : '') +
        (c.rating ? `<br/>★ ${escapeHtml(c.rating)}` : '') +
        (c.web ? `<br/><a href="${escapeHtml(c.web)}" target="_blank" rel="noreferrer">Sitio web</a>` : '');
      L.circleMarker([c.lat, c.lng], {
        radius: 8, color: '#fff', weight: 1.5, fillColor: COMP_COLOR, fillOpacity: 0.9,
      }).bindPopup(popup).addTo(layer);
      bounds.push([c.lat, c.lng]);
    });

    if (bounds.length > 1) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    } else {
      map.setView([centro.lat, centro.lng], 16);
    }
  }, [centro, competidores]);

  // Al cambiar el centro (Grido elegido, dirección o clic), recalcular todo.
  useEffect(() => {
    if (centro) {
      buscarCompetidores(centro, cuadras);
      cargarPoblacion(centro, cuadras);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centro?.lat, centro?.lng]);

  const buscarLocales = async () => {
    if (!zona.trim()) return;
    setLoadingLocales(true);
    setError('');
    setLocales([]);
    setGridoSel('');
    setCentroManual(null);
    setCompetidores([]);
    try {
      const res = await axios.get(API_BASE_URL + GRIDO_LOCALES, {
        params: { q: zona.trim() },
        headers: authHeader(),
      });
      const data = res.data?.data ?? {};
      const list = data.locales ?? [];
      setLocales(list);
      if (data.zona?.lat && mapRef.current) {
        mapRef.current.setView([data.zona.lat, data.zona.lng], 13);
      }
      if (list.length === 1) {
        setGridoSel(list[0].osm_id);
      } else if (list.length === 0) {
        setError('No se encontraron locales Grido en esa zona en OpenStreetMap (su cobertura es parcial). Ingresá la dirección de tu local o marcá el punto en el mapa.');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Error al buscar locales Grido.');
    } finally {
      setLoadingLocales(false);
    }
  };

  const buscarPorDireccion = async () => {
    if (!direccion.trim()) return;
    setLoadingGeo(true);
    setError('');
    setGridoSel('');
    try {
      const res = await axios.get(API_BASE_URL + GRIDO_GEOCODIFICAR, {
        params: { q: direccion.trim() },
        headers: authHeader(),
      });
      const d = res.data?.data;
      if (d && d.lat != null) {
        setCentroManual({ osm_id: 'manual', nombre: 'Tu local Grido', lat: d.lat, lng: d.lng, direccion: d.direccion, barrio: d.barrio, ciudad: d.ciudad });
      } else {
        setError('No se pudo ubicar esa dirección. Agregá ciudad y provincia.');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo ubicar esa dirección.');
    } finally {
      setLoadingGeo(false);
    }
  };

  const onCuadrasCommit = (_e, value) => {
    setCuadras(value);
    if (centro) {
      buscarCompetidores(centro, value);
      cargarPoblacion(centro, value);
    }
  };

  // Al cambiar la lista de competidores, resetear el estado de reseñas.
  useEffect(() => { setResenas({}); }, [competidores]);

  const toggleResenas = async (c) => {
    const actual = resenas[c.osm_id];
    // Ya cargado: solo abrir/cerrar.
    if (actual && (actual.data || actual.error)) {
      setResenas((prev) => ({ ...prev, [c.osm_id]: { ...actual, open: !actual.open } }));
      return;
    }
    setResenas((prev) => ({ ...prev, [c.osm_id]: { open: true, loading: true } }));
    try {
      const res = await axios.get(API_BASE_URL + GRIDO_RESENAS, {
        params: { osm_id: c.osm_id, nombre: c.nombre, lat: c.lat, lng: c.lng },
        headers: authHeader(),
      });
      setResenas((prev) => ({ ...prev, [c.osm_id]: { open: true, loading: false, data: res.data?.data ?? {} } }));
    } catch (e) {
      setResenas((prev) => ({ ...prev, [c.osm_id]: { open: true, loading: false, error: e.response?.data?.message || 'Error al traer reseñas.' } }));
    }
  };

  return (
    <Container maxWidth="xl" sx={{ pb: 6 }}>
      <MenuInicial />
      <TituloPagina
        icon={Icecream}
        titulo="Competencia Grido"
        subtitulo="Heladerías competidoras alrededor de un local Grido"
        ayuda="Elegí un local de la lista, escribí la dirección de tu Grido o hacé clic en el mapa. Los datos salen de OpenStreetMap (gratis) y su cobertura es parcial."
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        {/* Modo 1: buscar locales Grido en una zona (OpenStreetMap) */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Ciudad o zona"
            placeholder="Ej: Córdoba, Argentina"
            value={zona}
            onChange={(e) => setZona(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && buscarLocales()}
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Place fontSize="small" /></InputAdornment> }}
          />
          <Button
            variant="contained"
            startIcon={loadingLocales ? <CircularProgress size={18} color="inherit" /> : <Search />}
            onClick={buscarLocales}
            disabled={loadingLocales || !zona.trim()}
          >
            Buscar locales
          </Button>
        </Box>

        {locales.length > 0 && (
          <FormControl size="small" fullWidth sx={{ mt: 2 }}>
            <InputLabel>Local Grido ({locales.length})</InputLabel>
            <Select
              label={`Local Grido (${locales.length})`}
              value={gridoSel}
              onChange={(e) => { setGridoSel(e.target.value); setCentroManual(null); }}
              renderValue={(val) => {
                const l = locales.find((x) => x.osm_id === val);
                return l ? `${l.nombre} — ${ubicacionDe(l)}` : '';
              }}
            >
              {locales.map((l) => (
                <MenuItem key={l.osm_id} value={l.osm_id}>
                  <Box>
                    <Typography variant="body2" sx={{ lineHeight: 1.2 }}>{l.nombre}</Typography>
                    <Typography variant="caption" color="text.secondary">{ubicacionDe(l)}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Modo 2: dirección exacta del local (por si no está en OSM) */}
        <Divider sx={{ my: 2 }}>o</Divider>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Dirección de tu local Grido"
            placeholder="Ej: Av. Pellegrini 1234, Rosario, Santa Fe"
            value={direccion}
            onChange={(e) => setDireccion(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && buscarPorDireccion()}
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><MyLocation fontSize="small" /></InputAdornment> }}
          />
          <Button
            variant="outlined"
            startIcon={loadingGeo ? <CircularProgress size={18} color="inherit" /> : <Place />}
            onClick={buscarPorDireccion}
            disabled={loadingGeo || !direccion.trim()}
          >
            Usar dirección
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          También podés hacer clic en cualquier punto del mapa para fijar ahí el análisis.
        </Typography>

        {/* Radio en cuadras */}
        <Box sx={{ mt: 2, maxWidth: 360 }}>
          <Typography variant="caption" color="text.secondary">
            Radio: {cuadras} cuadra{cuadras !== 1 ? 's' : ''} (~{cuadras * 100} m)
          </Typography>
          <Slider
            value={cuadras}
            onChange={(_e, v) => setCuadras(v)}
            onChangeCommitted={onCuadrasCommit}
            min={1}
            max={30}
            step={1}
            valueLabelDisplay="auto"
            disabled={!centro}
          />
        </Box>
      </Paper>

      {error && <Alert severity="info" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Barra de métricas de la zona de influencia */}
      {centro && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Zona de influencia ({cuadras} cuadra{cuadras !== 1 ? 's' : ''}, ~{(Math.PI * Math.pow(cuadras * 100 / 1000, 2)).toFixed(2)} km²)
            </Typography>
            {poblacion.loading && <CircularProgress size={16} />}
          </Box>
          {poblacion.error && <Typography variant="caption" color="text.secondary">{poblacion.error}</Typography>}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {(() => {
              const estimados = [
                { label: 'Competidores', valor: competidores.length },
                { label: 'Población (2020)', valor: poblacion.data?.poblacion != null ? poblacion.data.poblacion.toLocaleString('es-AR') : (poblacion.loading ? '…' : '—') },
                { label: 'Densidad', valor: poblacion.data?.densidad != null ? `${poblacion.data.densidad.toLocaleString('es-AR')} hab/km²` : '—' },
                {
                  label: 'Habitantes por heladería',
                  valor: poblacion.data?.poblacion != null
                    ? Math.round(poblacion.data.poblacion / (competidores.length + 1)).toLocaleString('es-AR')
                    : '—',
                },
              ];
              return estimados.map((m) => (
                <Box key={m.label} sx={{ px: 2, py: 1, borderRadius: 1.5, bgcolor: 'action.hover', minWidth: 120, flex: '1 1 auto' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{m.valor}</Typography>
                  <Typography variant="caption" color="text.secondary">{m.label}</Typography>
                </Box>
              ));
            })()}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Población estimada por WorldPop (grilla ~100 m, año 2020). "Habitantes por heladería" incluye el propio Grido.
          </Typography>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Mapa */}
        <Paper variant="outlined" sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          <Box ref={containerRef} sx={{ height: { xs: 360, md: 560 }, width: '100%' }} />
          {loadingComp && (
            <Box sx={{
              position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.9)',
              borderRadius: 1, px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', gap: 1, zIndex: 1000,
            }}>
              <CircularProgress size={16} /> <Typography variant="caption">Buscando…</Typography>
            </Box>
          )}
        </Paper>

        {/* Panel lateral de competidores */}
        <Paper variant="outlined" sx={{ width: { xs: '100%', md: 340 }, maxHeight: { md: 560 }, overflow: 'auto' }}>
          <Box sx={{ p: 2, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Storefront fontSize="small" /> Competidores
              {centro && <Chip size="small" label={competidores.length} color="primary" />}
            </Typography>
            {centro && (
              <Typography variant="caption" color="text.secondary">
                Alrededor de {centro.nombre} — {ubicacionDe(centro)}
              </Typography>
            )}
          </Box>

          {!centro && (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Icecream sx={{ fontSize: 40, opacity: 0.4 }} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Elegí un local Grido, escribí una dirección o hacé clic en el mapa para ver su competencia.
              </Typography>
            </Box>
          )}

          {centro && !loadingComp && competidores.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">
                Sin heladerías competidoras a {cuadras} cuadras (según OpenStreetMap). Ampliá el radio.
              </Typography>
            </Box>
          )}

          <List dense disablePadding>
            {competidores.map((c, i) => {
              const r = resenas[c.osm_id] || {};
              return (
              <React.Fragment key={c.osm_id}>
                {i > 0 && <Divider component="li" />}
                <ListItem
                  alignItems="flex-start"
                  disableGutters
                  sx={{ flexDirection: 'column', alignItems: 'stretch', px: 1 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <ListItemButton
                      onClick={() => mapRef.current?.setView([c.lat, c.lng], 17)}
                      sx={{ borderRadius: 1, flex: 1 }}
                    >
                      <ListItemText
                        primary={c.nombre}
                        secondary={
                          <>
                            <span style={{ display: 'block' }}>{ubicacionDe(c)}</span>
                            {c.telefono && <span style={{ display: 'block' }}>Tel: {c.telefono}</span>}
                            {c.web && (
                              <Link href={c.web} target="_blank" rel="noreferrer" underline="hover">
                                Sitio web
                              </Link>
                            )}
                          </>
                        }
                      />
                    </ListItemButton>
                    <Chip size="small" label={`${c.distancia} m`} variant="outlined" sx={{ mt: 1 }} />
                  </Box>

                  <Button
                    size="small"
                    onClick={() => toggleResenas(c)}
                    startIcon={r.loading ? <CircularProgress size={14} /> : <RateReview fontSize="small" />}
                    endIcon={r.open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    sx={{ alignSelf: 'flex-start', textTransform: 'none', ml: 1, mb: 0.5 }}
                  >
                    Ver reseñas
                  </Button>

                  <Collapse in={!!r.open} unmountOnExit>
                    <Box sx={{ px: 2, pb: 1.5 }}>
                      {r.loading && <Typography variant="caption" color="text.secondary">Consultando Foursquare…</Typography>}
                      {r.error && <Alert severity="warning" sx={{ py: 0 }}>{r.error}</Alert>}
                      {r.data && r.data.configurado === false && (
                        <Alert severity="info" sx={{ py: 0 }}>
                          {r.data.mensaje || 'Configurá FOURSQUARE_API_KEY para ver reseñas.'}
                        </Alert>
                      )}
                      {r.data && r.data.configurado !== false && r.data.encontrado === false && (
                        <Typography variant="caption" color="text.secondary">
                          Sin datos de este comercio en Foursquare.
                        </Typography>
                      )}
                      {r.data && r.data.encontrado && (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            {r.data.rating5 != null ? (
                              <>
                                <Rating value={r.data.rating5} precision={0.1} size="small" readOnly />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.data.rating5}</Typography>
                                {r.data.total_ratings != null && (
                                  <Typography variant="caption" color="text.secondary">
                                    ({r.data.total_ratings} opiniones)
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <Typography variant="caption" color="text.secondary">Sin rating cargado</Typography>
                            )}
                          </Box>
                          {r.data.categoria && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              {r.data.categoria}
                            </Typography>
                          )}
                          {(r.data.tips || []).length > 0 ? (
                            (r.data.tips).map((t, idx) => (
                              <Typography key={idx} variant="caption" sx={{ display: 'block', mb: 0.75, fontStyle: 'italic' }}>
                                “{t.texto}”{t.fecha ? ` — ${t.fecha}` : ''}
                              </Typography>
                            ))
                          ) : (
                            <Typography variant="caption" color="text.secondary">Sin reseñas escritas.</Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </Collapse>
                </ListItem>
              </React.Fragment>
              );
            })}
          </List>
        </Paper>
      </Box>
    </Container>
  );
}

export default GridoCompetencia;
